import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classnames from 'classnames';
import ArrowLeftIcon from '@icons/ArrowLeftIcon';
import ArrowRightIcon from '@icons/ArrowRightIcon';
import CaretDownIcon from '@icons/CaretDownIcon';
import CaretUpIcon from '@icons/CaretUpIcon';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const styles = {
    textStyles: {
        fontFamily: 'Red Hat Display',
        color: '#000',
    },
    iconStyles: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
    },
};

const IconButton = styled.button`
    align-items: center;
    background: transparent;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    height: 40px;
    justify-content: center;
    width: 40px;
    color: #000;
    padding: 0;

    &:hover:not(.active) {
        background: #f5f5f5;
    }

    &.active {
        background: #246eff;
        color: #fff;
        cursor: default;
    }
`;

const TodayButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    font-family: 'Red Hat Display';
    font-size: 14px;
    font-weight: 700;
    line-height: 22px;
    text-align: center;
    color: #246eff;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
        text-decoration-thickness: 1.5px;
        text-underline-offset: 4px;
    }
`;

const CalendarToolbar = ({ label, date, onNavigate, onDateChange }) => {
    const [open, setOpen] = useState(false);
    const [year, setYear] = useState(date.getFullYear());
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handler = e => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleMonthSelect = month => {
        const next = new Date(year, month, 1);
        onDateChange(next);
        setOpen(false);
    };

    return (
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', gap: 16, alignItems: 'center' }}>
            <IconButton type="button" onClick={() => onNavigate('PREV')}>
                <ArrowLeftIcon />
            </IconButton>

            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    minWidth: 376,
                    justifyContent: 'center',
                }}
            >
                <span
                    style={{
                        ...styles.textStyles,
                        fontSize: 39,
                        fontWeight: 700,
                        lineHeight: '50px',
                        letterSpacing: '1.39px',
                        textAlign: 'center',
                    }}
                >
                    {label}
                </span>

                <IconButton type="button" onClick={() => setOpen(v => !v)} className={classnames({ active: open })}>
                    {open ? <CaretUpIcon color="white" /> : <CaretDownIcon />}
                </IconButton>
            </div>

            <IconButton type="button" onClick={() => onNavigate('NEXT')}>
                <ArrowRightIcon />
            </IconButton>

            <TodayButton type="button" onClick={() => onNavigate('TODAY')}>
                Today
            </TodayButton>

            {/* Year Selector Dropdown */}
            {open && (
                <div
                    ref={dropdownRef}
                    style={{
                        position: 'absolute',
                        top: 60,
                        padding: 8,
                        background: '#fff',
                        boxShadow: '0 0 7px 0 rgba(87, 87, 87, 0.20)',
                        borderRadius: 8,
                        zIndex: 9999,
                        width: 260,
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: 12,
                            alignItems: 'center',
                        }}
                    >
                        <IconButton type="button" onClick={() => setYear(y => y - 1)}>
                            <ArrowLeftIcon />
                        </IconButton>
                        <p
                            style={{
                                ...styles.textStyles,
                                fontWeight: 700,
                                fontSize: 18,
                                lineHeight: '25px',
                                letterSpacing: '0.31px',
                                margin: 0,
                            }}
                        >
                            {year}
                        </p>
                        <IconButton type="button" onClick={() => setYear(y => y + 1)}>
                            <ArrowRightIcon />
                        </IconButton>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}>
                        {MONTHS.map((month, i) => {
                            const isActive = date.getFullYear() === year && date.getMonth() === i;

                            return (
                                <button
                                    key={month}
                                    type="button"
                                    onClick={() => handleMonthSelect(i)}
                                    style={{
                                        ...styles.textStyles,
                                        ...styles.iconStyles,
                                        fontWeight: isActive ? 700 : 400,
                                        textTransform: 'uppercase',
                                        padding: '8px 12px',
                                    }}
                                >
                                    {month}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

CalendarToolbar.propTypes = {
    label: PropTypes.string.isRequired,
    onNavigate: PropTypes.func.isRequired,
    onDateChange: PropTypes.func.isRequired,
    date: PropTypes.instanceOf(Date).isRequired,
};

export default CalendarToolbar;
